import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRightLeft, Link2, Unlink, Sparkles, BellOff, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { API_BASE } from '@/utils/api';
import { STATE_META, displayName } from './tableStates';

const errMsg = (e) => e.response?.data?.detail || 'No se pudo completar la acción';

const Picker = ({ options, selected, toggle, single }) => (
  <div className="grid grid-cols-3 gap-2 max-h-60 overflow-auto" data-testid="table-picker">
    {options.length === 0 && <p className="col-span-3 text-sm text-muted-foreground">No hay mesas disponibles</p>}
    {options.map(u => (
      <label key={u.unit_id} data-testid={`table-pick-${u.unit_id}`}
        className={`flex items-center gap-2 border rounded-lg px-2 py-1.5 text-sm cursor-pointer ${selected.includes(u.unit_id) ? 'border-primary bg-primary/10' : 'border-border'}`}>
        <Checkbox checked={selected.includes(u.unit_id)} onCheckedChange={() => toggle(u.unit_id, single)} />
        <span className="truncate">{u.name}</span>
      </label>
    ))}
  </div>
);

export const TableActionsDialog = ({ unit, units, open, onOpenChange, onDone, onOpenDetails, onOpenTable, onRelease, detailsLabel = 'Ver cuenta y pedidos', renderExtra }) => {
  const [mode, setMode] = useState(null);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  if (!unit) return null;
  const primaryId = unit.group_primary_unit_id || unit.unit_id;
  const isGroup = unit.group_unit_ids?.length > 1;
  const toggle = (id, single) => setSelected(s => single ? [id] : s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const close = () => { setMode(null); setSelected([]); onOpenChange(false); };

  const run = async (fn, okText) => {
    setBusy(true);
    try { await fn(); toast.success(okText); onDone?.(); close(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const post = (path, body) => axios.post(`${API_BASE}${path}`, body);
  const freeTargets = units.filter(u => !u.session_id && u.unit_id !== unit.unit_id);
  const mergeTargets = units.filter(u => u.unit_id !== primaryId && !u.group_primary_unit_id && !['paying'].includes(u.state));

  const confirm = () => {
    if (mode === 'move') return run(() => post('/tables/move', { session_id: unit.session_id, target_unit_id: selected[0] }), 'Mesa cambiada');
    if (mode === 'merge') return run(() => post('/tables/merge', { primary_unit_id: primaryId, unit_ids: selected }), 'Mesas unidas');
    if (mode === 'split') return run(() => post('/tables/split', { session_id: unit.session_id, unit_ids: selected.length ? selected : null }), 'Mesas separadas');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(v) : close())}>
      <DialogContent className="sm:max-w-md bg-card" data-testid="table-actions-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{displayName(unit)}</DialogTitle>
          <DialogDescription data-testid="table-actions-state">{STATE_META[unit.state]?.label}</DialogDescription>
        </DialogHeader>
        {!mode && (
          <div className="grid gap-2">
            {!unit.session_id && onOpenTable && <Button data-testid="table-action-open" onClick={() => { onOpenTable(unit); close(); }}>Abrir mesa</Button>}
            {unit.session_id && onOpenDetails && <Button variant="outline" data-testid="table-action-details" onClick={() => { onOpenDetails(unit); close(); }}>{`${detailsLabel}`}</Button>}
            {renderExtra?.(unit)}
            {unit.pending_confirmation && <Button data-testid="table-action-confirm-table" disabled={busy} onClick={() => run(() => post(`/tables/sessions/${unit.session_id}/confirm`), 'Mesa confirmada')}>Confirmar mesa{unit.pending_orders ? ` (${unit.pending_orders} pedidos en espera)` : ''}</Button>}
            {unit.state === 'waiter_call' && <Button data-testid="table-action-attend" disabled={busy} onClick={() => run(() => post(`/tables/sessions/${unit.session_id}/attend`), 'Llamado atendido')}><BellOff className="w-4 h-4 mr-2" />Atender llamado</Button>}
            {unit.session_id && !isGroup && <Button variant="outline" data-testid="table-action-move" onClick={() => setMode('move')}><ArrowRightLeft className="w-4 h-4 mr-2" />Cambiar de mesa</Button>}
            {unit.state !== 'paying' && <Button variant="outline" data-testid="table-action-merge" onClick={() => setMode('merge')}><Link2 className="w-4 h-4 mr-2" />Juntar con otras mesas</Button>}
            {isGroup && <Button variant="outline" data-testid="table-action-split" onClick={() => setMode('split')}><Unlink className="w-4 h-4 mr-2" />Separar mesas</Button>}
            {unit.session_id && onRelease && <Button variant="ghost" className="text-red-600" data-testid="table-action-release" onClick={() => { onRelease(unit); close(); }}>Liberar sin cobrar</Button>}
            {unit.state === 'to_clean' && <Button data-testid="table-action-clean" disabled={busy} onClick={() => run(() => post(`/tables/${unit.unit_id}/clean`), 'Mesa lista')}><Sparkles className="w-4 h-4 mr-2" />Marcar limpia</Button>}
          </div>
        )}
        {mode === 'move' && <><p className="text-sm">Elegí la mesa libre destino. Se mueve la sesión completa.</p><Picker options={freeTargets} selected={selected} toggle={toggle} single /></>}
        {mode === 'merge' && <><p className="text-sm">Elegí las mesas a juntar con {unit.name}. Quedan con una sola cuenta.</p><Picker options={mergeTargets} selected={selected} toggle={toggle} /></>}
        {mode === 'split' && <><p className="text-sm">Elegí qué mesas separar (sin elegir: se separan todas).</p><Picker options={units.filter(u => unit.group_unit_ids.includes(u.unit_id) && u.unit_id !== primaryId)} selected={selected} toggle={toggle} /></>}
        {mode && (
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMode(null); setSelected([]); }} data-testid="table-action-back">Volver</Button>
            <Button onClick={confirm} disabled={busy || (mode !== 'split' && !selected.length)} data-testid="table-action-confirm">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Confirmar
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
